import React, { useCallback, useEffect, useState } from 'react';
import { PhoneCall, Circle, Loader2, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { voiceAPI } from '@/lib/api';

const STATE_META = {
  on_call: { label: 'On call', dot: 'text-rose-500 fill-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  available: { label: 'Available', dot: 'text-emerald-500 fill-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  unavailable: { label: 'Unavailable', dot: 'text-amber-500 fill-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  offline: { label: 'Offline', dot: 'text-slate-300 fill-slate-300', chip: 'bg-slate-50 text-slate-500 border-slate-200' },
};

export default function VoiceSupervisionPage() {
  const [data, setData] = useState({ agents: [], summary: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await voiceAPI.supervision();
      setData(r.data);
    } catch (_) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const s = data.summary || {};

  return (
    <div data-testid="voice-supervision-page">
      <PageHeader
        title="Live call center"
        description="Real-time agent availability and who is currently on a call. Refreshes automatically."
        breadcrumbs={[{ label: 'Settings', href: '/platform-settings' }, { label: 'Calling', href: '/settings/voice' }, { label: 'Live', href: '/settings/voice/supervision' }]}
      />
      <div className="page-content space-y-4 max-w-4xl">
        <div className="flex flex-wrap items-center gap-3">
          {['on_call', 'available', 'unavailable', 'offline'].map((k) => (
            <div key={k} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${STATE_META[k].chip}`} data-testid={`supervision-count-${k}`}>
              <Circle size={9} className={STATE_META[k].dot} /> {STATE_META[k].label}: {s[k] || 0}
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={load} className="ml-auto" data-testid="supervision-refresh"><RefreshCw size={13} className="mr-1" /> Refresh</Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : data.agents.length === 0 ? (
          <Card><CardContent className="py-14 text-center text-sm text-slate-500" data-testid="supervision-empty">
            <PhoneCall size={22} className="mx-auto mb-2 text-slate-300" />
            No softphone agents have connected yet.
          </CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.agents.map((a) => {
              const meta = STATE_META[a.state] || STATE_META.offline;
              return (
                <Card key={a.user_id} data-testid={`supervision-agent-${a.user_id}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold shrink-0">
                      {(a.name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{a.name || 'Unknown'}</div>
                      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-2 py-0.5 mt-0.5 ${meta.chip}`}>
                        <Circle size={8} className={meta.dot} /> {meta.label}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
